import {
    forwardRef,
    KeyboardEvent,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import { isEmpty } from '@rainbow-modules/validation';
import AttachFileButton from 'components/AttachFileButton';
import AttachedFiles from 'components/AttachedFiles';
import HelpMeWrite from 'components/HelpMeWrite';
import SendIcon from 'components/icons/send';
import MicrophoneIcon from 'components/icons/microphone';
import FrequentMessage from 'components/FrequentMessage';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import useAttachFiles from 'hooks/useAttachFiles';
import DictateForm from './DictateForm';
import { IMAGE_FORMATS_ACCEPTED_STRING } from '../../constants';
import {
    Container, SendButton, ActionsContainer,
    MessageInput, InputContainer, ActionButton,
} from './styled';

interface ChatInputProps {
    className?: string;
    onSend?: (note: string, files: File[]) => Promise<void>;
    disableSend?: boolean;
    placeholder?: string;
    mode?: 'notes' | 'messages';
    disableAttachments?: boolean;
    acceptedFormats?: string;
    canAttachFile?: (file: File, acceptedFormats: string) => boolean;
    onFilesRejected?: (files: File[]) => void;
}

const SendNoteForm = forwardRef(
    ({
        className,
        onSend = async () => {},
        disableSend = false,
        placeholder = 'Write a note here...',
        mode = 'notes',
        disableAttachments = false,
        acceptedFormats = IMAGE_FORMATS_ACCEPTED_STRING,
        canAttachFile = () => true,
        onFilesRejected = () => {},
    }: ChatInputProps, ref) => {
        const [value, setValue] = useState('');
        const inputRef = useRef<HTMLTextAreaElement>();
        const normCleanValue = useMemo(() => (value || '').trim(), [value]);
        const { agentId = '' } = useParams();
        const {
            files: attachedFiles,
            attach: attachFiles,
            remove: removeAttachedFile,
            clear: clearAttachedFiles,
        } = useAttachFiles({
            onFilesRejected,
            canAttachFile,
            acceptedFormats,
        });

        const {
            isLoading: isTranscribing,
            mutateAsync: transcribeAudio,
        } = useMultipartMutation<FormData, { transcription?: any }>({
            method: 'POST',
            pathname: `/agents/${agentId}/transcribe`,
        });

        const setInputValue = (text: string) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line max-len
            const textarea = inputRef.current.fieldRef.current.textareaRef.current;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLTextAreaElement.prototype,
                'value',
            ).set;
            nativeInputValueSetter?.call(textarea, text);
            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
            setTimeout(() => inputRef.current?.focus(), 0);
        };

        const [isDictating, setIsDictating] = useState(false);

        const handleDictateAccept = async (blob: Blob | null) => {
            setIsDictating(false);
            if (blob) {
                try {
                    const formData = new FormData();
                    formData.append('audio', blob);
                    const { transcription } = await transcribeAudio({
                        body: formData,
                    });
                    if (transcription) {
                        setValue(transcription);
                    }
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
            }
        };

        const handleDictateClick = useCallback(() => setIsDictating(true), []);

        const handleDictateCancel = useCallback(() => setIsDictating(false), []);

        const sendText = useCallback(
            async () => {
                try {
                    await onSend(normCleanValue, attachedFiles);
                    setValue('');
                    clearAttachedFiles();
                } catch (error) {
                    // no catch
                }
            },
            [onSend, clearAttachedFiles, attachedFiles, normCleanValue],
        );

        const handleKeyDown = useCallback(
            (event: KeyboardEvent<HTMLTextAreaElement>) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (/\S/.test(normCleanValue)) {
                        sendText();
                    }
                }
            },
            [normCleanValue, sendText],
        );

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus();
            },
        }));

        if (isDictating) {
            return (
                <DictateForm
                    className={className}
                    onAccept={handleDictateAccept}
                    onCancel={handleDictateCancel}
                />
            );
        }

        const disableElements = isTranscribing || disableSend;
        const disableSubmit = disableElements || !(normCleanValue || attachedFiles.length);

        return (
            <Container className={className}>
                <InputContainer>
                    <MessageInput
                        variant="shaded"
                        placeholder={placeholder}
                        value={value}
                        grow
                        rows={1}
                        onChange={(event) => setValue(event.target.value)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        disabled={disableElements}
                    />
                    <RenderIf isTrue={!disableAttachments && attachedFiles.length > 0}>
                        <AttachedFiles
                            files={attachedFiles}
                            onRemoveFile={removeAttachedFile}
                            disabled={disableElements}
                        />
                    </RenderIf>
                    <ActionsContainer>
                        <RenderIf isTrue={!disableAttachments}>
                            <AttachFileButton
                                acceptedFormats={acceptedFormats}
                                onAttachFiles={attachFiles}
                                disabled={disableElements}
                            />
                        </RenderIf>
                        <FrequentMessage
                            context={mode}
                            onSelectMessage={setInputValue}
                            agentId={agentId}
                            disabled={disableElements}
                        />
                        <ActionButton
                            tooltip="Dictate"
                            icon={<MicrophoneIcon />}
                            onClick={handleDictateClick}
                            disabled={disableElements}
                        />
                        <RenderIf isTrue={!isEmpty(value)}>
                            <HelpMeWrite
                                value={value}
                                onInsert={setInputValue}
                                agentId={agentId}
                                disabled={disableElements}
                            />
                        </RenderIf>
                    </ActionsContainer>
                </InputContainer>
                <SendButton
                    variant="brand"
                    icon={<SendIcon />}
                    onClick={sendText}
                    disabled={disableSubmit}
                />
            </Container>
        );
    },
);

export default SendNoteForm;
